import { ARProviderProps } from "@animech/ar";
import { Theme } from "@animech/ui";
import {
	AppSceneSettings,
	nil,
	PartialRootPropertyUpdateTree,
	SetCameraViewAction,
	UiFavicon,
} from "@animech/axiom-format";
import {
	format,
	getPropertyName,
	isInSubtree,
	makeResourceUrl,
	PropertyMap,
	resolveProperty,
	transformValue,
} from "@animech/axiom-utility";
import { Entity, GraphNode } from "playcanvas";
import {
	PropertyState,
	PropertyStateValues,
} from "@animech/axiom-configurator";
import { stateMapToTree } from "@animech/axiom-configurator/src/utility";
import { isSceneUrl, normalizeSceneUrl } from "@animech/axiom-scene-utilities";
import { ConfiguratorBridge } from "./configurator-bridge";
import { DataStorage } from "./data-storage";
import { Router } from "./router";
import { mappedPreferences, propertyPreferences } from "./preferences";
import { UnitSystem } from "./types";
import {
	_migrateAndDeserialize,
	ConfigurationDelta,
	currentSessionDataVersion,
	serializedNil,
	serializedUndefined,
	SessionData,
} from "./configuration-migrations";

import { generateProductsInStockDomain } from "./articles/stock-check";
import { isUiView, UiManager, UiRenderer, UiView } from "src/ui";
import {
	ConfigurationMode,
	WallSnapSettings,
	SceneManager,
	WallVisualizationSettings,
	CameraMode,
	RoomAppearance,
	roomAppearanceFromData,
} from "src/scene";
import { generatedRoomEnabled } from "src/scene/room/room-enabled";
import {
	makeUiManagerDefinitions,
	logConfigurationChanged,
	isUiProperty,
	isProperty,
	BuildInfo,
	makeConfigurator,
	Configurator,
	ConfiguratorChange,
	makeLocalizer,
	makeMetaRegistry,
	ConfigurationHistoryManager,
	restoreStoredConfiguration,
	appendCacheBuster,
} from "src/utility";
import {
	Definitions,
	Locale,
	Localization,
	ProductId,
	Root,
	root,
	createRules,
} from "src/definitions";
import {
	materialThemeUniverse,
	StockRef,
} from "src/definitions/type-definitions";
import {
	Property,
	PropertyValue,
	PriceAmount,
	AnyWallEditingToolType,
	ProductStructure,
	UiStructure,
	PropertyChangeMap,
	ActionContext,
	Localizer,
	MetaRegistry,
	ModelData,
	CameraView,
	UiSettings,
	SceneStructure,
} from "src/common";
import { MeasurementRenderer } from "src/ui/measurement-renderer";
import {
	IncomingMessageData,
	IncomingMessagePayloads,
	OutgoingMessageEventType,
	PostMessageHandler,
} from "src/postmessage-handler";
import {
	ApiArticles,
	ApiDataManager,
	ArticleNumbersColorAndQuantity,
} from "src/api";
import { PreferencesManager } from "src/utility/preferences-manager";
import { isSpecialModelParserNode } from "src/scene/colliders/utility";
import { generateArticleNumbersAndQuantities } from "src/core/articles";

type UiNavigation = {
	view: UiView;
	section?: string;
};

type LoadingTasks = {
	startup: number;
	application: number;
	scene: number;
};

type ApplicationArgs = {
	domRoot: string;
	resourceBaseUrl: string;
	minLoadingTime?: number;
	useLocalStorage?: boolean;
	arConfig?: ARProviderProps;
	theme: Theme;
	modelData: ModelData;
	settingsFromQuery: SettingsFromQuery;
	autoCameraFocusEnabled: boolean;
};

type Config = {
	defaultRoute: string;
	playCanvasAssetPath: string;
	playCanvasConfigPath: string;
	changeNotificationMinCount: number;
	changeNotificationMaxCount: number;
	cameraTransitionDuration: number;
	initialCameraTransitionDuration: number;
	arErrorQueryParam: string;
	configurationMode: ConfigurationMode;
	cameraMode: CameraMode;
	plannerEnabled: boolean;
	sessionDataDeltaType: ConfigurationDeltaType;
	disableRenderingOnIdle: boolean;
};

export const config: Config = {
	defaultRoute: "/",
	playCanvasAssetPath: "playcanvas",
	playCanvasConfigPath: appendCacheBuster("config.json"),
	changeNotificationMinCount: Infinity,
	changeNotificationMaxCount: 2,
	cameraTransitionDuration: 1,
	initialCameraTransitionDuration: 1,
	arErrorQueryParam: "ar_error",
	configurationMode: "room",
	plannerEnabled: true,
	cameraMode: "orbit",
	sessionDataDeltaType: "full",
	disableRenderingOnIdle: true,
};

type AnyAppSceneSettings = AppSceneSettings<string>;

/**
 * Data stored in local storage that may be restored when reloading the app.
 *
 * If this object needs to change, for example because new information needs to be stored or the format of the configuration changes,
 * please make it a union of all possible formats with the corresponding version hardcoded in each object, like this:
 * { version: "1.1.0", ... } | { version: "1.0.1", ... }, etc.
 */
type ConfigurationDeltaType = "full" | "partial";

export type Scene = "main" | "environment";

export type SettingsFromQuery = {
	lan: Locale;
	guid: string;
	pricelistid: string | null;
	vat: string | null;
	countrycode: string | null;
	designtemplate: string | null;
	unitsystem: UnitSystem;
};

export type QuerySettingsError = {
	errorMessage: string;
};

export class Application {
	private _router: Router;
	private _configurator: Configurator;
	private _initialConfigurationState: PropertyState<Root, Root, ["root"]>;
	private _lastConfigurationState: PropertyState<Root, Root, ["root"]>;
	private _configuratorBridge: ConfiguratorBridge;
	private _localizer: Localizer;
	private _metaRegistry: MetaRegistry;
	private _uiManager: UiManager;
	private _sceneManager: SceneManager;
	private _arConfig?: ARProviderProps;
	private _activeView?: UiView;
	private _activeScene?: Scene;
	private _activeProperty: Property;
	private _dataStorage: DataStorage;
	private _routerInactive = true;
	private _sectionCameraView?: CameraView;
	private _lastActiveCameraView?: CameraView;
	private _wasStoredConfigLoaded: boolean = false;
	private _loadingTasks: LoadingTasks = {
		startup: 0,
		scene: 0,
		application: 0,
	};
	private _configurationHistoryManager = new ConfigurationHistoryManager();
	private _preferencesManager: PreferencesManager;
	private _postMessageHandler: PostMessageHandler;
	private _apiDataManager: ApiDataManager;
	private _inStockProducts: Record<string, PropertyValue[]> = {};
	private _stockDataLoaded = false;
	private _stockRef: StockRef = { current: null };
	private _originalApiArticles: ApiArticles | undefined;
	private _arExportModel: Entity | undefined;
	private _unitSystem: UnitSystem;

	constructor({
		domRoot,
		resourceBaseUrl,
		minLoadingTime,
		useLocalStorage,
		arConfig,
		theme,
		modelData,
		settingsFromQuery,
		autoCameraFocusEnabled,
	}: ApplicationArgs) {
		this._router = new Router();
		this._router.addListener(this._onRouteChanged);
		this._dataStorage = new DataStorage(useLocalStorage ?? false);
		this._unitSystem = settingsFromQuery.unitsystem;

		const uiStructure: UiStructure = Definitions.Ui.ui;
		this._activeProperty = uiStructure?.settings?.initialActiveProperty ?? root;

		if (!this._dataStorage.isPersistent) {
			console.warn(
				"Configuration data will not persist on page refresh. This is either due to lack of localStorage support or blocked cookies in the browser.",
			);
		}

		this._metaRegistry = makeMetaRegistry(
			Definitions.Product.propertyMeta,
			resourceBaseUrl,
		);

		this._arConfig = arConfig && {
			...arConfig,
			exportOptions: {
				includeNode: (node: GraphNode) =>
					!node.tags.has("excludeFromAR") && !isSpecialModelParserNode(node),
			},
			onInitExport: this._createARExportModel,
			onExportDone: () => {
				this._arExportModel?.destroy();
				this._arExportModel = undefined;
				this._sceneManager.sceneEngine.autoRenderManager.pushSingleFrameRequest();
			},
		};

		const navigationFromUrlPath = Application._navigationFromUrlPath(
			this._router.path,
		);

		this._addManifest(
			makeResourceUrl(
				Definitions.General.appSettings.manifest,
				resourceBaseUrl,
			),
		);

		if (uiStructure.favicons && uiStructure.favicons.length > 0) {
			this._removeFavicons();
			for (const favicon of uiStructure.favicons) {
				this._addFavicon(favicon, resourceBaseUrl);
			}
		}
		const uiDefinitions = makeUiManagerDefinitions(
			Definitions.Ui.ui,
			resourceBaseUrl,
		);

		this._localizer = makeLocalizer(
			Definitions.Locale.localization as Localization,
			settingsFromQuery.lan,
		);

		this._configurator = makeConfigurator(root, createRules(this._stockRef), {
			valueAdjustment: "keep-first",
		});

		this._initialConfigurationState = this._configurator.get(root).clone();

		this._lastConfigurationState = this._initialConfigurationState;

		this._configuratorBridge = new ConfiguratorBridge(this._configurator);
		this._configuratorBridge.addConfigurationChangeListener(
			this._onPropertySet,
		);

		if (navigationFromUrlPath?.section) {
			this._configurator.set(root.uiSection, navigationFromUrlPath.section);
		}

		document.title = this._localizer.localizeTerm("pageTitle");

		const canvas = document.createElement("canvas");

		const wallSnapSettings: WallSnapSettings = {
			snapToGrid: false,
			gridSpacing: 1,
			nodeSnapDistance: 0.4,
			nodeAxisSnapDistance: 0.2,
		};
		const wallVisualizationSettings: WallVisualizationSettings = {
			fadeObscuringWalls: false,
		};

		const measurementRenderer = new MeasurementRenderer(
			canvas,
			false,
			this._unitSystem,
		);

		this._preferencesManager = new PreferencesManager(
			this._configurator,
			this._getInStockDomainFromProductId,
			mappedPreferences,
		);

		this._apiDataManager = new ApiDataManager({
			settingsFromQuery,
			getSerializedConfiguration: () => {
				const storedConfiguration: SessionData = {
					version: currentSessionDataVersion,
					configurationDelta: this._getSerializedConfiguration(),
					...this._roomSessionData(),
				};
				return storedConfiguration;
			},
			setFromStoredConfiguration: async (sessionData: SessionData) => {
				const { sceneBuilder } = this._sceneManager;

				// Clear the selection around the load, the way undo/redo do.
				// SceneManager auto-selects the first selectable object a scene
				// update creates, so a loaded design otherwise comes up on the
				// outline layer, as though the user had clicked it.
				//
				// This callback is the one path every openDesign takes — the
				// parent-initiated postMessage, the `?designtemplate=` query
				// parameter and the in-app Open-Design modal — so owning the
				// contract here is what makes all three behave the same. It also
				// puts the trailing clear provably after the load: the callers
				// cannot do that, because `openDesign` does not await this
				// callback, so a clear placed after it races the load.
				this._onSelect();

				// Reset and wait for rebuild before loading the stored
				// configuration, to prevent potential issues when re-using
				// parts of the old configuration & scene graph.
				this._configurator.reset();
				this._onConfigurationChanged(true);
				await sceneBuilder.asyncOperationsFinished();

				this._loadStoredConfiguration(sessionData);
				// _loadStoredConfiguration sets configurator state via
				// _restoreStoredConfiguration but doesn't rebuild the scene.
				// Trigger _onConfigurationChanged so buildProduct() fires
				// and _lastConfigurationState stays in sync.
				this._onConfigurationChanged(true);
				this._onSelect();
			},
			generateSnapshot: async () => {
				return await this._sceneManager.renderToImageWithFocusView();
			},
			designIdentityStorage: this._dataStorage.designIdentity,
		});

		this._postMessageHandler = new PostMessageHandler({
			onIncomingMessageCallback: this._onIncomingPostMessageEventCallback,
		});

		this._uiManager = new UiManager({
			configurator: this._configurator,
			configuratorBridge: this._configuratorBridge,
			domRoot,
			resourceBaseUrl,
			theme,
			canvas,
			metaRegistry: this._metaRegistry,
			localizer: this._localizer,
			definitions: uiDefinitions,
			arConfig: this._arConfig,
			hasEnvironmentScene: this._sceneSettings.environment !== undefined,
			initView: navigationFromUrlPath?.view ?? "configurator",
			plannerEnabled: config.plannerEnabled,
			configurationMode: config.configurationMode,
			wallSnapSettings,
			wallVisualizationSettings,
			activeWallEditingTool: undefined,
			cameraMode: config.cameraMode,
			measurementsEnabled: measurementRenderer.enabled,
			unitSystem: this._unitSystem,
			dragOperationActive: false,
			configurationHistoryManager: this._configurationHistoryManager,
			settingsFromQuery,
			apiDataManager: this._apiDataManager,
			onAction: this._onAction,
			onSetScene: this._setScene,
			onActive: this._onActive,
			onViewChanged: this._onViewChanged,
			onGenerateCartImage: this._onGenerateCartImage,
			onRestart: this._onRestart,
			onCheckout: this._onCheckout,
			onEmitPostMessage: this._onEmitOutgoingPostMessageEvent,
			onPickEntities: this._onPickEntities,
			onSelect: this._onSelect,
			onDeleteSelected: this._onDeleteSelected,
			canDeleteSelected: this._canDeleteSelected,
			onSetConfigurationMode: this._onSetConfigurationMode,
			onOpenExternalLink: this._onOpenExternalLink,
			onCreateRoom: this._onCreateRoom,
			onSetWallColor: (name) => this._setRoomAppearance({ wallColor: name }),
			onSetFloorStyle: (name) => this._setRoomAppearance({ floorStyle: name }),
			onAddDoorToSelectedWall: this._onAddDoorToSelectedWall,
			onAddWindowToSelectedWall: this._onAddWindowToSelectedWall,
			onModifyWallSnapSettings: this._onModifyWallSnapSettings,
			onModifyWallVisualizationSettings:
				this._onModifyWallVisualizationSettings,
			onSetActiveWallEditingTool: this._onSetActiveWallEditingTool,
			getUrl: this._getUrl,
			getPrice: this._getPrice,
			onProductSelect: this._onProductSelect,
			onSetCameraMode: this._onSetCameraMode,
			onResetCameraFocus: this._onResetCameraFocus,
			onToggleMeasurementsEnabled: this._onToggleMeasurementsEnabled,
			onSetUnitSystem: this._onSetUnitSystem,
			onToggleDragOperationActive: this._onToggleDragOperationActive,
			onUndoCallback: this._onUndo,
			onRedoCallback: this._onRedo,
			onExportToGLTFCallback: this._onExportToGLTFCallback,
			getInStockDomainFromColorPropertyCallback:
				this._getInStockDomainFromColorProperty,
			getInStockDomainFromProductIdCallback:
				this._getInStockDomainFromProductId,
			getStockHiddenValuesCallback: this._getStockHiddenValues,
			onChangeLocale: this._onChangeLocale,
		});

		if (BuildInfo.mode === "development") {
			this._uiManager.setDevToolsData({
				stockEditor: {
					getApiArticles: () => this._apiDataManager.apiArticles,
					getOriginalApiArticles: () =>
						this._originalApiArticles ?? this._apiDataManager.apiArticles,
					applyArticles: this._devApplyStockArticles,
				},
			});
		}

		const uiRenderer = new UiRenderer(this._uiManager, canvas);

		this._sceneManager = new SceneManager({
			configurator: this._configurator,
			sceneDefinitions: Definitions.Scene.scene as SceneStructure,
			canvas,
			assetBaseUrl: makeResourceUrl(
				config.playCanvasAssetPath,
				resourceBaseUrl,
			),
			configPath: config.playCanvasConfigPath,
			configurationMode: config.configurationMode,
			cameraMode: config.cameraMode,
			activeProperty: uiDefinitions.initialActiveProperty,
			uiRenderer,
			measurementRenderer,
			wallSnapSettings,
			wallVisualizationSettings,
			modelData,
			onAction: this._onAction,
			onSetActiveWallEditingTool: this._onSetActiveWallEditingTool,
			onSetCameraMode: this._onSetCameraMode,
			onToggleDragOperationActive: this._onToggleDragOperationActive,
			onCameraIsTransitioningChanged: this._onCameraIsTransitioningChanged,
			onInstantiateProductTemplate: this._onProductSelect,
			autoRenderEnabled: config.disableRenderingOnIdle,
			autoCameraFocusEnabled,
		});

		const encodedConfiguration = new URLSearchParams(this._router.query).get(
			"configuration",
		);
		let didRestoreStoredSession = false;
		if (encodedConfiguration) {
			this._setFromSessionData(encodedConfiguration);
			this._router.updateQuery("");
		} else {
			const sessionData = this._dataStorage.sessionData.get();
			if (sessionData && !settingsFromQuery.designtemplate) {
				didRestoreStoredSession = this._setFromSessionData(sessionData);
			}
		}
		this._router.updateQuery("");
		this._apiDataManager.initDesignIdentity(didRestoreStoredSession);

		this._onSectionChanged(this._getUiSection());

		if (minLoadingTime !== undefined) {
			this._pushLoadingTask("startup");
			setTimeout(() => {
				this._popLoadingTask("startup");
			}, minLoadingTime);
		}

		this._routerInactive = false;

		this._addToHistory();

		this._uiManager.render();

		// Inform parent window that the application is ready to be displayed
		this._postMessageHandler.emit(OutgoingMessageEventType.BYO_LOADED);

		this._initiateApi();

		if (settingsFromQuery.designtemplate) {
			this._apiDataManager.openDesign(settingsFromQuery.designtemplate);
		}
	}

	private _initiateApi() {
		this._apiDataManager.initApi().then(() => {
			if (!this._originalApiArticles) {
				this._originalApiArticles = JSON.parse(
					JSON.stringify(this._apiDataManager.apiArticles),
				);
			}
			this._generateProductsInStockDomain();
			this._updateArticles();
			this._uiManager.render();
			this._sceneManager.refreshOverlays();
		});
	}

	private _onChangeLocale = (locale: Locale) => {
		if (this._localizer.locale === locale) {
			return;
		}
		this._localizer.locale = locale;
		this._apiDataManager.language = locale;
		this._initiateApi();
	};
	private _generateProductsInStockDomain() {
		this._inStockProducts = generateProductsInStockDomain(
			this._apiDataManager.apiArticles,
			BuildInfo.mode === "development",
		);
		this._stockDataLoaded = true;
		this._stockRef.current = this._inStockProducts as Record<
			string,
			import("src/definitions/type-definitions").MaterialTheme[]
		>;
		const ver = (this._configurator.get(root.stockVersion).value as number) + 1;
		this._configurator.set(root.stockVersion, ver);
		this._onConfigurationChanged();
	}

	private _devApplyStockArticles = (articles: ApiArticles) => {
		this._apiDataManager._devSetApiArticles(articles);
		this._inStockProducts = generateProductsInStockDomain(articles, false);
		this._stockDataLoaded = true;
		this._stockRef.current = this._inStockProducts as Record<
			string,
			import("src/definitions/type-definitions").MaterialTheme[]
		>;
		const ver = (this._configurator.get(root.stockVersion).value as number) + 1;
		this._configurator.set(root.stockVersion, ver);
		this._onConfigurationChanged();
		this._sceneManager.refreshOverlays();
	};

	private _getInStockDomainFromProductId = (
		productId: ProductId,
	): PropertyValue[] => {
		if (!this._stockDataLoaded) {
			return materialThemeUniverse;
		}
		return this._inStockProducts[productId] ?? [];
	};
	private _resolveProductTypeForThemeProperty(
		colorProperty: Property,
	): string | undefined {
		const name = getPropertyName(colorProperty);

		if (name === "tableTheme" || name === "materialTheme") {
			return this._configurator.get(root.tableType).value as string;
		}
		if (name === "chairTheme") {
			// Extract chair array index from property path:
			// getPath() returns ["root", "chair", "<index>", "chairTheme"]
			const path = colorProperty.getPath();
			const chairIndex = Number(path[2]);
			if (!isNaN(chairIndex)) {
				return this._configurator.get(root.chair[chairIndex].chairType)
					.value as string;
			}
		}
		return undefined;
	}

	private _getInStockDomainFromColorProperty = (colorProperty: Property) => {
		if (!colorProperty || !this._configurator.exists(colorProperty)) {
			return [];
		}

		const stateTree = this._configurator.get(colorProperty).stateTree;
		const { domain } = stateTree;

		// Ignore undefined or domains of type RangeDomain.
		if (!domain || "min" in domain || "max" in domain) {
			return [];
		}

		if (!this._stockDataLoaded) {
			return domain;
		}

		const productType = this._resolveProductTypeForThemeProperty(colorProperty);
		if (!productType) {
			return domain;
		}

		const inStock = this._inStockProducts[productType] ?? [];
		return domain.filter((v) => inStock.includes(v));
	};

	private _getStockHiddenValues = (
		property: Property,
		fieldValues: unknown[],
	): unknown[] => {
		if (!this._stockDataLoaded) {
			return [];
		}

		const name = getPropertyName(property);

		if (
			name === "materialTheme" ||
			name === "tableTheme" ||
			name === "chairTheme"
		) {
			const inStock = this._getInStockDomainFromColorProperty(property);
			return fieldValues.filter((v) => !inStock.includes(v));
		}
		if (name === "tableType" || name === "chairType") {
			return fieldValues.filter(
				(v) => this._getInStockDomainFromProductId(v as ProductId).length === 0,
			);
		}
		return [];
	};

	private _onIncomingPostMessageEventCallback = (
		incomingMessage: IncomingMessageData,
	) => {
		const { event, data } = incomingMessage;
		if (!this._uiManager) {
			return;
		}

		switch (event) {
			case "enableCart":
				this._uiManager.setCartEnabled(true);
				break;
			case "enableEstimate":
				this._uiManager.setEstimateEnabled(true);
				break;
			case "enableTemplates":
				this._uiManager.setTemplatesEnabled(true);
				break;
			case "disableVAT":
				this._uiManager.setVATEnabled(false);
				break;
			case "openDesign":
				{
					const typedData = data as IncomingMessagePayloads["openDesign"];
					this._openDesignFromParent(typedData.designId);
				}
				break;
		}
	};

	private _openDesignFromParent = async (designId: string) => {
		await this._apiDataManager.openDesign(designId);
	};

	private get _onEmitOutgoingPostMessageEvent(): PostMessageHandler["emit"] {
		return this._postMessageHandler.emit.bind(this._postMessageHandler);
	}

	private get _sceneSettings(): AnyAppSceneSettings {
		return Definitions.General.appSettings.scene;
	}

	private _serializeConfiguration(
		configuration: PartialRootPropertyUpdateTree<Root>,
	) {
		return transformValue(configuration, (value) => {
			if (Array.isArray(value)) {
				const arrayWithNil: unknown[] = [];
				for (let i = 0; i < value.length; ++i) {
					arrayWithNil[i] = i in value ? value[i] : nil;
				}
				return { update: arrayWithNil };
			} else {
				return value === undefined
					? { replace: serializedUndefined }
					: value === nil
						? { replace: serializedNil }
						: undefined;
			}
		});
	}

	private _getSessionData(
		propertiesToExclude: Property[],
		encode: boolean = true,
	): string {
		let configurationDelta: PartialRootPropertyUpdateTree<Root>;
		if (config.sessionDataDeltaType === "full") {
			configurationDelta = this._configurator.get(root).stateTree;
		} else {
			const delta = this._initialConfigurationState.getDelta(
				this._configurator.get(root),
			).stateMap;
			const filteredDelta = new PropertyMap<Root, PropertyStateValues<unknown>>(
				[...delta].filter(
					([property]) => !propertiesToExclude.some((p) => p.equals(property)),
				),
			);
			configurationDelta = stateMapToTree(filteredDelta);
		}
		const sessionData: SessionData = {
			version: "1.0.0",
			configurationDelta: this._serializeConfiguration(configurationDelta),
			...this._roomSessionData(),
		};

		const stringifiedSessionData = JSON.stringify(sessionData);
		return encode ? btoa(stringifiedSessionData) : stringifiedSessionData;
	}

	private _decodeSavedSessionData(
		configuration: string,
	): SessionData | undefined {
		try {
			const decoded = atob(configuration);
			return JSON.parse(decoded) as SessionData;
		} catch {
			// Not base64 encoded, try plain JSON
		}

		try {
			return JSON.parse(configuration) satisfies SessionData;
		} catch {
			return undefined;
		}
	}

	/** @returns whether the configuration was restored. */
	private _setFromSessionData(encodedSessionData: string): boolean {
		const sessionData = this._decodeSavedSessionData(encodedSessionData);

		// Skip the restore rather than throwing: this runs from the constructor,
		// so a throw here escapes it and leaves the bootstrapper with no
		// application at all — no canvas, and the loading task never popped.
		// _decodeSavedSessionData is deliberately written not to throw, and an
		// undecodable value (a truncated ?configuration= URL, or a stale
		// localStorage entry that would fail on every reload) is not a reason to
		// deny the user a working configurator.
		if (!sessionData) {
			console.warn(
				`Failed decoding session data, continuing without restoring. The value parsed as neither base64-encoded JSON nor plain JSON.`,
			);
			return false;
		}

		return this._loadStoredConfiguration(sessionData);
	}

	/** @returns whether the configuration was restored. */
	private _loadStoredConfiguration(sessionData: SessionData): boolean {
		if (generatedRoomEnabled) {
			this._applyRoomAppearance(roomAppearanceFromData(sessionData.room));
		}
		try {
			this._wasStoredConfigLoaded = true;
			const deserializedData = _migrateAndDeserialize(sessionData);
			this._restoreStoredConfiguration(deserializedData);
			// Clear the restore's history entry, then re-seed the restored
			// configuration as the undo baseline so undo stays disabled until the
			// user makes the first change (clearHistory alone would leave no
			// baseline, requiring two changes before undo re-enables).
			this._configurationHistoryManager.clearHistory();
			this._addToHistory();
			return true;
		} catch (error) {
			this._wasStoredConfigLoaded = false;
			console.warn(
				`Failed restoring configuration, continuing without restoring. The error was:`,
				error,
			);
		}
		return false;
	}

	private _getSerializedConfiguration = () => {
		return this._serializeConfiguration(
			this._configurator.get(root).clone().stateTree,
		);
	};

	private _setFromSerializedConfiguration = (
		version: SessionData["version"],
		serializedConfiguration?: ConfigurationDelta,
	) => {
		if (!serializedConfiguration) {
			return;
		}

		try {
			const deserializedConfiguration = _migrateAndDeserialize({
				version,
				configurationDelta: serializedConfiguration,
			});
			this._restoreStoredConfiguration(deserializedConfiguration);
			this._onConfigurationChanged();
		} catch (error) {
			console.warn(
				`Failed restoring state from history, continuing without restoring. The error was:`,
				error,
			);
		}
	};

	private _addToHistory = () => {
		const config = this._getSerializedConfiguration();
		// Strip UI navigation state from history snapshots so undo/redo
		// only restores product configuration, never which section is active.
		delete (config as Record<string, unknown>).uiSection;
		this._configurationHistoryManager.addToHistory(config);
	};

	private _onUndo = () => {
		const prev = this._configurationHistoryManager.getPrevious();
		this._setFromSerializedConfiguration(currentSessionDataVersion, prev);
		this._onSelect();
	};

	private _onRedo = () => {
		const next = this._configurationHistoryManager.getNext();
		this._setFromSerializedConfiguration(currentSessionDataVersion, next);
		this._onSelect();
	};

	private static _navigationToUrlPath(navigation: UiNavigation): string {
		if (navigation.section === undefined) {
			return navigation.view;
		}

		return `${navigation.view}/${navigation.section}`;
	}

	private static _navigationFromUrlPath(
		urlPath: string,
	): UiNavigation | undefined {
		const splitUrlPath = urlPath.split("/");
		const viewPart = splitUrlPath[0];
		const sectionPart = splitUrlPath[1];

		if (!isUiView(viewPart)) {
			return undefined;
		}

		return {
			view: viewPart,
			section: sectionPart,
		};
	}

	public render(): void {
		this._uiManager.render();
	}

	private _pushLoadingTask(key: keyof LoadingTasks) {
		const wasLoading = this._loadingTasks[key]++ > 0;
		if (!wasLoading) {
			switch (key) {
				case "startup":
					this._uiManager.toggleStartupLoading(true);
					break;
				case "application":
					this._uiManager.toggleApplicationLoading(true);
					break;
				case "scene":
					this._uiManager.toggleSceneLoading(true);
					break;
			}
		}
	}

	private _popLoadingTask(key: keyof LoadingTasks) {
		const isLoading = --this._loadingTasks[key] > 0;
		if (!isLoading) {
			switch (key) {
				case "startup":
					this._uiManager.toggleStartupLoading(false);
					break;
				case "application":
					this._uiManager.toggleApplicationLoading(false);
					break;
				case "scene":
					this._uiManager.toggleSceneLoading(false);
					break;
			}
		}
	}

	private _removeFavicons() {
		const iconElems = Array.from(document.getElementsByTagName("link")).filter(
			(elem) => ["icon", "apple-touch-icon"].includes(elem.rel),
		);
		for (const iconElem of iconElems) {
			iconElem.remove();
		}
	}

	private _addFavicon<TImage extends string>(
		favicon: UiFavicon<TImage>,
		baseUrl: string,
	) {
		const size = `${favicon.size}x${favicon.size}`;
		const href = baseUrl + favicon.image;

		const link = document.createElement("link");
		link.rel = "icon";
		link.type = favicon.type;
		link.href = href;
		link.sizes.add(size);

		const appleLink = document.createElement("link");
		appleLink.rel = "apple-touch-icon";
		appleLink.href = href;
		appleLink.sizes.add(size);

		document.head.appendChild(link);
		document.head.appendChild(appleLink);
	}

	private _addManifest(url: string) {
		const link = document.createElement("link");
		link.rel = "manifest";
		link.href = url;
		document.head.appendChild(link);
	}

	private _getUiSection(): string | undefined {
		return this._configurator.get(root.uiSection).value;
	}

	private _getScenePath(scene: Scene): string | undefined {
		let scenePath;
		switch (scene) {
			case "environment":
				scenePath = this._sceneSettings.environment;
				break;
			default:
				scenePath = this._sceneSettings.main;
		}
		if (!scenePath) {
			return undefined;
		}

		if (isSceneUrl(scenePath)) {
			return appendCacheBuster(normalizeSceneUrl(scenePath));
		}

		return scenePath;
	}

	private _configurationChangesAreSceneRelated = (
		changes: PropertyChangeMap,
	): boolean => {
		return (
			[...changes.keys()].some(
				(p) =>
					getPropertyName(p) === "nodeTransforms" || // TODO This is a fix for exluding properties generated from Vespa demo but should be handled in a generic way
					getPropertyName(p) === "materialTransforms",
			) ||
			[...changes.keys()].some((property) =>
				this._sceneManager.isSceneProperty(property),
			)
		);
	};

	private _notifyConfigurationChanged = (
		setProperty: Property,
		change: ConfiguratorChange,
	) => {
		const presentableProperties: string[] = [];
		for (const [property, _info] of change.properties) {
			if (
				isUiProperty(property) &&
				!property.equals(setProperty) &&
				!property.equals(root.uiSection)
			) {
				presentableProperties.push(this._localizer.localizeProperty(property));
			}
		}
		const numPresentableProperties = presentableProperties.length;
		const notificationText = (() => {
			if (numPresentableProperties === 1) {
				return format(
					this._localizer.localizeTerm("changeNotificationSingle"),
					[presentableProperties[0]],
				);
			} else if (
				numPresentableProperties <= config.changeNotificationMaxCount
			) {
				return format(
					this._localizer.localizeTerm("changeNotificationMultiple"),
					[
						presentableProperties.slice(0, -1).join(", "),
						presentableProperties.slice(-1)[0],
					],
				);
			} else {
				return format(
					this._localizer.localizeTerm(
						"changeNotificationMultipleWithOverflow",
					),
					[
						presentableProperties
							.slice(0, config.changeNotificationMaxCount)
							.join(", "),
						numPresentableProperties - config.changeNotificationMaxCount,
					],
				);
			}
		})();
		if (numPresentableProperties >= config.changeNotificationMinCount) {
			this._uiManager.showFieldChangeNotification(notificationText);
		}
	};

	private _setScene = async (scene: Scene) => {
		const scenePath = this._getScenePath(scene);
		if (!scenePath) {
			console.warn("No scene to set");
			return;
		}

		if (this._sceneManager.activeScenePath !== scenePath) {
			this._pushLoadingTask("scene");

			const isInitialScene = !this._sceneManager.sceneCreated;
			const startView = this._sceneManager.systemViews.initial;

			await this._sceneManager.createScene(scenePath, startView);

			if (isInitialScene) {
				await this._sceneManager.buildProduct();
				if (this._arConfig) {
					this._arConfig.exportModel =
						this._sceneManager.rootEntity || undefined;
				}

				this._sceneManager.renderAreaDescription(
					this._configurator.get(root.areaWidth).value,
					this._configurator.get(root.areaDepth).value,
				);
			}

			this._popLoadingTask("scene");
		}

		// Always play transition if scene changes, even if scenePath remains
		// the same. The wizard and main views might use the same scene path
		// but we still want the initial transition when changing views.
		if (this._activeScene !== scene) {
			this._playInitialCameraTransition();
		}
		this._activeScene = scene;
	};

	private _playInitialCameraTransition() {
		const startView = this._sceneManager.systemViews.initial;
		const endView = this._sectionCameraView
			? this._sectionCameraView
			: this._sceneManager.systemViews.target;

		if (endView) {
			if (startView) {
				this._sceneManager.setCameraViewFrom(
					startView,
					endView,
					config.initialCameraTransitionDuration,
				);
			} else {
				this._sceneManager.setCameraView(
					endView,
					config.cameraTransitionDuration,
				);
			}

			this._lastActiveCameraView = endView;
		}

		this._onStoredConfigLoaded();
	}

	private _onGenerateCartImage = (image: HTMLImageElement) => {
		const view = this._sceneManager.systemViews.cart;

		image.setAttribute("draggable", "false");
		image.src = this._sceneManager.renderToImage(
			image.width,
			image.height,
			view,
		);
	};

	private _onActive = (node: Record<string, unknown>, active: boolean) => {
		if (!active) {
			return;
		}

		const events = node.events as
			| { active?: ActionContext["action"] }
			| undefined;
		if (events?.active) {
			this._runAction({
				action: events.active,
				archetype: this._activeProperty,
			});
			this._sectionCameraView = this._lastActiveCameraView;
			return;
		}

		if (this._autoLinkViews && node.state) {
			const property = node.state as Property;
			const viewId = getPropertyName(property);
			const fieldView = this._sceneManager.tryGetCameraView(viewId);
			const defaultView =
				this._sectionCameraView ?? this._sceneManager.systemViews.target;

			if (fieldView && this._lastActiveCameraView !== fieldView) {
				this._sceneManager.setCameraView(
					fieldView,
					config.cameraTransitionDuration,
				);
				this._lastActiveCameraView = fieldView;
			} else if (
				!fieldView &&
				defaultView &&
				this._lastActiveCameraView !== defaultView
			) {
				this._sceneManager.setCameraView(
					defaultView,
					config.cameraTransitionDuration,
				);
				this._lastActiveCameraView = defaultView;
			}
		}
	};

	private _onRestart = (nextView: UiView) => {
		this._lastActiveCameraView = undefined;
		this._sectionCameraView = undefined;
		this._configuratorBridge.resetConfiguration();
		this._configurationHistoryManager.clearHistory();
		this._apiDataManager.setDesignId("");
		this._onViewChanged(nextView);
	};

	private _getUrl = (): string => {
		const trimmedUrl = this._router.url.replace(/\/+$/, "");
		const hash = trimmedUrl.includes("#") ? "" : "/#/";

		const { designId, guid, pricelistId, vatOverride, countryCode } =
			this._apiDataManager;
		const queryParams: SettingsFromQuery = {
			designtemplate: designId,
			lan: this._localizer.locale as Locale,
			unitsystem: this._unitSystem,
			guid,
			pricelistid: pricelistId,
			vat: vatOverride,
			countrycode: countryCode,
		};
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(queryParams)) {
			if (value != null) {
				params.set(key, value);
			}
		}
		return `${trimmedUrl}${hash}?${params.toString()}`;
	};

	private _getPrice = (
		_property: Property,
		_value?: PropertyValue,
		_valueDiff?: boolean,
	): PriceAmount | undefined => {
		return undefined;
	};

	private _onPropertySet = (property: Property, change: ConfiguratorChange) => {
		if (this._activeView === "configurator" && property !== root) {
			this._notifyConfigurationChanged(property, change);
			this._preferencesManager.updatePreferencesFromProperties(
				propertyPreferences,
				[property],
			);
		}
		// Sidebar navigation (accordion open/close) should never create history entries.
		const isNavigation = property.equals(root.uiSection);
		this._onConfigurationChanged(isNavigation);
	};

	private _onConfigurationChanged(disableHistory: boolean = false) {
		const currentConfigurationState = this._configurator.get(root).clone();
		const delta = this._lastConfigurationState.getDelta(
			currentConfigurationState,
		);
		const changes = delta.changes;

		if (BuildInfo.mode !== "production") {
			logConfigurationChanged(changes);
		}

		if (this._sceneManager.sceneCreated) {
			if (this._configurationChangesAreSceneRelated(changes)) {
				this._sceneManager.buildProduct();
				this._onStoredConfigLoaded();
			}
		}

		if (changes.has(root.uiSection)) {
			this._onSectionChanged(this._getUiSection());
		}

		if (
			this._sceneManager.sceneCreated &&
			(changes.has(root.areaDepth) || changes.has(root.areaWidth))
		) {
			this._sceneManager.renderAreaDescription(
				this._configurator.get(root.areaWidth).value,
				this._configurator.get(root.areaDepth).value,
			);
			this._sceneManager.buildProduct();
		}

		if (!disableHistory && changes.size > 0) {
			this._addToHistory();
		}

		this._storeConfiguration();
		// Refresh the article list (which drives the displayed price) BEFORE
		// rendering, so the render reflects the current configuration. Rendering
		// first left the price one config-change behind — invisible while a later
		// render happened to follow (e.g. a button click's React re-render), but
		// stale after an async configuration change (undo/redo) where no such
		// follow-up render occurs.
		this._updateArticles();

		this._uiManager.render();

		this._lastConfigurationState = currentConfigurationState;
	}

	// Since the rootEntity is positioned with origo at 0,0,0 and the actual configuration is always located
	// on the wall away from the origo, we will move the root entity based on the bb of the configuration so that
	// the exported model is always centered on the screen initially.
	private _createARExportModel = (): Promise<Entity | undefined> => {
		const { rootEntity, configurationBoundingBox } = this._sceneManager;

		if (!rootEntity) {
			return Promise.resolve(undefined);
		}
		this._arExportModel = rootEntity.clone();

		if (configurationBoundingBox) {
			const { center } = configurationBoundingBox;
			this._arExportModel.translate(-center.x, 0, -center.z);
		}

		// The entity will be ignored by the AR module if it is not part of the scene tree, so we add it here.
		// It will be destroyed and removed when the exporter is done.
		rootEntity.addChild(this._arExportModel);

		this._postMessageHandler.emit(OutgoingMessageEventType.OPEN_IN_AR);
		return Promise.resolve(this._arExportModel);
	};

	private _updateArticles = () => {
		// It's important this emit is done after this._storeConfiguration() so that
		// this._getUrl() returns an up to date state of the configuration.
		this._apiDataManager.onConfigurationChanged(
			this._generateArticleNumbersAndQuantities(),
			(exportData) => {
				this._postMessageHandler.emit(OutgoingMessageEventType.DESIGN_UPDATE, {
					data: {
						...exportData,
						url: this._getUrl(),
					},
				});
			},
		);
	};

	private _storeConfiguration() {
		this._dataStorage.sessionData.set(this._getSessionData([]));
	}

	private get _autoLinkViews(): boolean {
		return (
			(Definitions.Scene.scene as SceneStructure).camera.settings
				.autoLinkViews ?? true
		);
	}

	private _onSectionChanged(section: string | undefined) {
		if (!this._routerInactive && this._activeView) {
			this._router.updatePath(
				Application._navigationToUrlPath({
					view: this._activeView,
					section,
				}),
			);
		}
		if (section && this._autoLinkViews) {
			const sectionView = this._sceneManager.tryGetCameraView(section);
			if (sectionView && this._lastActiveCameraView !== sectionView) {
				this._sceneManager.setCameraView(
					sectionView,
					config.cameraTransitionDuration,
				);
				this._sectionCameraView = sectionView;
				this._lastActiveCameraView = sectionView;
			} else {
				this._sectionCameraView = undefined;
			}
		} else {
			this._sectionCameraView = undefined;
		}
	}

	private _onViewChanged = (nextView: UiView) => {
		this._activeView = nextView;

		if (!this._routerInactive) {
			this._router.updatePath(
				Application._navigationToUrlPath({
					view: nextView,
					section: this._getUiSection(),
				}),
			);
		}
	};

	private _onRouteChanged = () => {
		const navigation = Application._navigationFromUrlPath(this._router.path);
		if (navigation) {
			this._routerInactive = true;
			this._uiManager.setView(navigation.view);
			this._configurator.set(root.uiSection, navigation.section ?? undefined);
			this._routerInactive = false;
		}
	};

	private _onCheckout = () => {
		this._postMessageHandler.emit(OutgoingMessageEventType.CART_UPDATE, {
			data: {
				...this._apiDataManager.exportData,
				url: this._getUrl(),
			},
		});
	};

	private _onPickEntities = (
		x: number,
		y: number,
		width = 1,
		height = 1,
	): Promise<Entity[]> => {
		return this._sceneManager.pickEntities(x, y, width, height);
	};

	private _onSelect = (...entities: Entity[]) => {
		// Guard on the scene: SceneManager.select() dereferences
		// sceneEngine.autoRenderManager, which does not exist until
		// createScene() has run. That is reachable — a shop page sends
		// openDesign as soon as it sees byoLoaded (the documented handshake),
		// and on mobile the Open-Design modal is open at boot — and the throw
		// is silent, rejecting an un-awaited async caller as an unhandled
		// rejection with no /open request made. Nothing can be outlined before
		// a scene exists, so skipping the call then is also semantically
		// correct.
		if (!this._sceneManager.sceneCreated) {
			return;
		}

		this._sceneManager.select(...entities);
	};

	private _onDeleteSelected = () => {
		const result = this._sceneManager.deleteSelected();
		if (result) {
			this._onConfigurationChanged();
		}
		return result;
	};

	private _canDeleteSelected = () => {
		return this._sceneManager.canDeleteSelected();
	};

	private _onToggleMeasurementsEnabled = () => {
		const enabled = !this._sceneManager.measurementRenderer.enabled;
		this._sceneManager.measurementRenderer.enabled = enabled;
		this._uiManager.setMeasurementsEnabled(enabled);
	};

	private _onSetUnitSystem = (system: UnitSystem) => {
		this._unitSystem = system;
		this._sceneManager.measurementRenderer.unitSystem = system;
		this._uiManager.setUnitSystem(system);
	};

	private _onToggleDragOperationActive = (active: boolean) => {
		this._sceneManager.toggleDragOperationActive(active);
		this._uiManager.toggleDragOperationActive(active);
	};

	private _onAction = (context: ActionContext) => {
		const configurationChanged = this._runAction(context);
		if (configurationChanged) {
			this._onConfigurationChanged();
		}
	};

	private _runAction(context: ActionContext): boolean {
		const action = context.action;
		let configurationChanged = false;
		if ("setCameraView" in action) {
			const setCameraViewAction = action as SetCameraViewAction<Root>;
			const target = setCameraViewAction.setCameraView;

			const isObjectTarget = typeof target === "object" && "id" in target;
			const idValue = isObjectTarget ? target.id : target;
			const skipIfActive = isObjectTarget && target.skipIfActive;

			const viewId = isProperty(idValue)
				? (this._configurator.get(resolveProperty(idValue, context.archetype))
						.value as string)
				: idValue;

			const view = this._sceneManager.tryGetCameraView(viewId);
			if (view && (!skipIfActive || this._lastActiveCameraView !== view)) {
				this._sceneManager.setCameraView(view, config.cameraTransitionDuration);
				this._lastActiveCameraView = view;
			}
			return configurationChanged;
		}
		const type = action.type;
		if (type === "setActiveProperty" || type === "setProperty") {
			const property = resolveProperty(
				action.property as Property,
				context.archetype,
				type === "setProperty" && action.targetArrayElement,
			);
			if (type === "setActiveProperty") {
				this._onSetActiveProperty(property);
			} else if (type === "setProperty") {
				if (isInSubtree(this._activeProperty, property)) {
					const parent = property.getParent();
					if (parent) {
						const parentState = this._configurator.get(parent);
						const elementProperties = Object.keys(parentState.stateTree).map(
							(index) => (parent as unknown as Record<string, Property>)[index],
						);
						const newActiveProperty = elementProperties.find(
							(p) => !p.equals(property),
						);
						this._onSetActiveProperty(newActiveProperty ?? root);
					}
				}
				this._configurator.set(property, action.value);
				configurationChanged = true;
			}
		} else if (type === "list") {
			if (action.execution === "parallel") {
				throw new Error(`Parallel list action execution is not implemented`);
			}
			for (const subaction of action.subactions) {
				const subresult = this._runAction({
					action: subaction,
					archetype: context.archetype,
				});
				configurationChanged = configurationChanged || subresult;
			}
		} else {
			console.error(`Unhandled action of type ${type}`);
		}
		return configurationChanged;
	}

	private _onSetConfigurationMode = (mode: ConfigurationMode) => {
		if (mode === "walls") {
			const uiSettings = Definitions.Ui.ui.settings as UiSettings;
			const activeProperty = uiSettings.defaultWallsProperty;
			if (activeProperty) {
				this._onSetActiveProperty(activeProperty);
			} else {
				console.warn(
					`Unable to change to "walls" mode, no "defaultWallsProperty" specified in UI definition.`,
				);
				return;
			}
		}

		this._sceneManager.setConfigurationMode(mode);
		this._uiManager.updateConfigurationMode(mode);
	};

	private _roomSessionData(): Pick<SessionData, "room"> {
		return generatedRoomEnabled
			? { room: this._sceneManager.roomAppearance }
			: {};
	}

	private _applyRoomAppearance(appearance: RoomAppearance) {
		this._sceneManager.setWallColor(appearance.wallColor);
		this._sceneManager.setFloorStyle(appearance.floorStyle);
		this._uiManager.updateRoomAppearance(appearance);
	}

	private _setRoomAppearance(appearance: Partial<RoomAppearance>) {
		this._applyRoomAppearance({
			...this._sceneManager.roomAppearance,
			...appearance,
		});
		this._storeConfiguration();
	}

	private _onCreateRoom = (width: number, depth: number) => {
		return this._sceneManager.createRoom(width, depth);
	};

	private _onAddDoorToSelectedWall = () => {
		return this._sceneManager.addDoorToSelectedWall();
	};

	private _onAddWindowToSelectedWall = () => {
		return this._sceneManager.addWindowToSelectedWall();
	};

	private _onModifyWallSnapSettings = (settings: Partial<WallSnapSettings>) => {
		const resolvedSettings = {
			...this._sceneManager.wallSnapSettings,
			...settings,
		};
		this._sceneManager.onWallSnapSettingsChanged(resolvedSettings);
		this._uiManager.updateWallSnapSettings(resolvedSettings);
	};

	private _onModifyWallVisualizationSettings = (
		settings: Partial<WallVisualizationSettings>,
	) => {
		const resolvedSettings = {
			...this._sceneManager.wallVisualizationSettings,
			...settings,
		};
		this._sceneManager.onWallVisualizationSettingsChanged(resolvedSettings);
		this._uiManager.updateWallVisualizationSettings(resolvedSettings);
	};

	private _onSetActiveWallEditingTool = (
		type: AnyWallEditingToolType | undefined,
	) => {
		this._sceneManager.onActiveWallEditingToolChanged(type);
		this._uiManager.updateActiveWallEditingTool(type);
	};

	private _onSetCameraMode = (mode: CameraMode, forceViewReset?: boolean) => {
		this._sceneManager.onSetCameraMode(mode, forceViewReset);
		this._uiManager.updateCameraMode(mode);
	};

	private _onResetCameraFocus = (focusOnSelection: boolean, time?: number) => {
		this._sceneManager.resetCameraFocus(focusOnSelection, time);
	};

	private async _onStoredConfigLoaded() {
		if (!this._wasStoredConfigLoaded) {
			return;
		}

		await this._sceneManager.sceneBuilder.asyncOperationsFinished();
		this._onResetCameraFocus(false);
		this._wasStoredConfigLoaded = false;
	}

	private _onCameraIsTransitioningChanged = (transitioning: boolean) => {
		this._uiManager.updateCameraIsTransitioning(transitioning);
	};

	private _onOpenExternalLink(url: string) {
		window.open(url, "_blank");
	}

	private _onSetActiveProperty = (property: Property) => {
		this._activeProperty = property;
		this._sceneManager.onActivePropertyChanged(property);
		this._uiManager.setActiveProperty(property);
	};

	private _onProductSelect = (
		productId: ProductId,
		disableHistory?: boolean,
	) => {
		const productStructure = Definitions.Product
			.products as unknown as ProductStructure;
		const product = productStructure.items[productId];
		const template = product.template;
		if (template) {
			/* Find some free index in the possibly sparse product array and
			create the new product there. */
			const resolvedProperty = resolveProperty(
				template,
				this._activeProperty,
			) as Property<unknown[]>;
			const currentState = this._configurator.get(resolvedProperty).stateTree;
			let availableIndex: number | undefined;
			for (let i = 0; i < currentState.length; ++i) {
				if (!(i in currentState)) {
					availableIndex = i;
				}
			}
			if (availableIndex === undefined) {
				availableIndex = currentState.length;
			}

			const newElementProperty = resolvedProperty[availableIndex];
			const change = this._configurator.set(newElementProperty, product.value);
			const changedProperties = [...change.properties.keys()];

			this._preferencesManager.applyPreferencesToProperties(
				propertyPreferences,
				changedProperties,
			);
			this._onConfigurationChanged(disableHistory);

			return newElementProperty;
		}

		return undefined;
	};

	private _restoreStoredConfiguration(
		targetTree: PartialRootPropertyUpdateTree<Root>,
	) {
		restoreStoredConfiguration(this._configurator, targetTree);
	}

	private _onExportToGLTFCallback = async () => {
		this._pushLoadingTask("scene");
		await this._sceneManager.exportToGLTF();
		this._popLoadingTask("scene");
	};

	private _generateArticleNumbersAndQuantities(): ArticleNumbersColorAndQuantity[] {
		const tableType = this._configurator.get(root.tableType).value;
		const tableMaterialWood = this._configurator.get(
			root.tableMaterialWood,
		).value;
		const tableMaterialMetal = this._configurator.get(
			root.tableMaterialMetal,
		).value;
		const chairs = this._configurator.get(root.chair).value;

		return generateArticleNumbersAndQuantities({
			tableType,
			tableMaterialWood,
			tableMaterialMetal,
			chairs,
		});
	}
}
